import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import { logger } from '@/lib/logger'

interface UseInfiniteScrollOptions {
  onLoadMore: () => Promise<void>
  hasMore: boolean
  threshold?: number
  enabled?: boolean
  container?: string // Selector for scroll container
  debug?: boolean
  debugLabel?: string
  containerRef?: RefObject<HTMLElement | null>
}

export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  threshold = 400,
  enabled = true,
  container = '[data-scroll-container]',
  debug = false,
  debugLabel,
  containerRef,
}: UseInfiniteScrollOptions) {
  const [isLoading, setIsLoading] = useState(false)
  const isFetchingRef = useRef(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const logPrefix = debugLabel ? `[useInfiniteScroll:${debugLabel}]` : '[useInfiniteScroll]'
  const containerNode = containerRef?.current ?? null

  const logDebug = useCallback(
    (message: string, details?: Record<string, unknown>) => {
      if (!debug) return
      if (details) logger.info(`${logPrefix} ${message}`, details)
      else logger.info(`${logPrefix} ${message}`)
    },
    [debug, logPrefix]
  )

  const handleLoadMore = useCallback(async () => {
    logDebug('load-requested', {
      hasMore,
      enabled,
      isFetching: isFetchingRef.current,
    })
    if (isFetchingRef.current) {
      logDebug('load-skipped', { reason: 'already-fetching' })
      return
    }
    if (!hasMore) {
      logDebug('load-skipped', { reason: 'no-more-results' })
      return
    }
    if (!enabled) {
      logDebug('load-skipped', { reason: 'disabled' })
      return
    }

    isFetchingRef.current = true
    setIsLoading(true)
    logDebug('load-started')

    try {
      await onLoadMore()
      logDebug('load-success')
    } catch (error) {
      logDebug('load-error', {
        message: error instanceof Error ? error.message : String(error),
      })
      throw error
    } finally {
      isFetchingRef.current = false
      setIsLoading(false)
      logDebug('load-finished')
    }
  }, [onLoadMore, hasMore, enabled, logDebug])

  useEffect(() => {
    logDebug('init', { hasMore, enabled, threshold, container })
  }, [logDebug, hasMore, enabled, threshold, container])

  // Scroll-based detection
  useEffect(() => {
    if (!enabled) return

    const scroller = containerNode ?? (document.querySelector(container) as HTMLElement | null)
    if (!scroller) {
      logDebug('no-scroll-container', { container, hasRef: Boolean(containerRef?.current) })
      return
    }

    logDebug('scroll-listener-attached', {
      scrollHeight: scroller.scrollHeight,
      clientHeight: scroller.clientHeight,
    })

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true

      requestAnimationFrame(() => {
        const nearBottom =
          scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight) < threshold

        if (nearBottom && hasMore && !isFetchingRef.current) {
          logDebug('near-bottom', {
            hasMore,
            isFetching: isFetchingRef.current,
            scrollTop: scroller.scrollTop,
            clientHeight: scroller.clientHeight,
            scrollHeight: scroller.scrollHeight,
          })
          handleLoadMore()
        } else if (nearBottom) {
          logDebug('near-bottom-no-load', {
            hasMore,
            isFetching: isFetchingRef.current,
          })
        }
        ticking = false
      })
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      scroller.removeEventListener('scroll', onScroll)
      logDebug('scroll-listener-detached')
    }
  }, [enabled, hasMore, threshold, handleLoadMore, container, logDebug, containerNode])

  // IntersectionObserver for sentinel
  useEffect(() => {
    if (!enabled) return

    const scroller = containerNode ?? (document.querySelector(container) as HTMLElement | null)
    const sentinel = sentinelRef.current
    if (!scroller || !sentinel) {
      logDebug('observer-skipped', {
        hasScroller: Boolean(scroller),
        hasSentinel: Boolean(sentinel),
      })
      return
    }

    logDebug('observer-attached')

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && hasMore && !isFetchingRef.current) {
          logDebug('sentinel-visible', {
            hasMore,
            isFetching: isFetchingRef.current,
          })
          handleLoadMore()
        } else if (entry.isIntersecting) {
          logDebug('sentinel-visible-no-load', {
            hasMore,
            isFetching: isFetchingRef.current,
          })
        }
      },
      { root: scroller, rootMargin: `${threshold}px 0px`, threshold: 0 }
    )

    io.observe(sentinel)
    return () => {
      io.disconnect()
      logDebug('observer-detached')
    }
  }, [enabled, hasMore, threshold, handleLoadMore, container, logDebug, containerNode])

  return {
    isLoading,
    sentinelRef,
  }
}

